import { ReactElement, useMemo, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { useRunAuxSensorData } from 'hooks/useRuns';
import { getWheelCircumference } from 'utils/channelNamesGridded';
import {
  POWER_PLOT,
  WIND_PLOT,
  SpeedMode,
  DataMode,
  buildPowerSeries,
  buildSpeedSeries,
  buildComparisonScatter,
  buildPowerComparisonScatter,
  buildTraverseSegments,
  hasTraverseData,
  maxWheelSpeedMs,
  speedUnit,
} from './auxSensorChannels';
import AuxSensorPlot from './AuxSensorPlot';
import AuxComparisonPlot from './AuxComparisonPlot';
import AuxRatioForcePlot from './AuxRatioForcePlot';
import AuxRatioTimePlot from './AuxRatioTimePlot';
import CollapsibleSection from './CollapsibleSection';

interface AuxSensorContext {
  run?: { id?: string } | null;
  calibration?: any;
}

const DEFAULT_SPEED_THRESHOLD_MS = 2;

const SPEED_MODE_OPTS = [
  { value: 'rotational', label: 'Rotational' },
  { value: 'linear', label: 'Linear' },
];
const DATA_MODE_OPTS = [
  { value: 'all', label: 'All' },
  { value: 'traverse', label: 'Traverse' },
];

function ModeRadio({
  name,
  value,
  options,
  onChange,
}: {
  name: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}): ReactElement {
  return (
    <div className="cr-flex cr-gap-4">
      {options.map((o) => (
        <label key={o.value} className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={value === o.value}
            onChange={() => onChange(o.value)}
            className="cr-cursor-pointer"
            style={{ accentColor: '#03365D', width: '16px', height: '16px' }}
          />
          <span className="cr-text-sm cr-font-medium">{o.label}</span>
        </label>
      ))}
    </div>
  );
}

export default function RunAuxSensor(): ReactElement {
  const { runId: runIdParam } = useParams<{ runId: string }>();
  const { run, calibration } = useOutletContext<AuxSensorContext>();
  const runId = runIdParam ?? run?.id ?? null;

  const [speedMode, setSpeedMode] = useState<SpeedMode>('rotational');
  const [dataMode, setDataMode] = useState<DataMode>('all');
  const [speedThreshold, setSpeedThreshold] = useState(DEFAULT_SPEED_THRESHOLD_MS);

  const { data: auxRun, isPending } = useRunAuxSensorData(runId);
  const gridded = auxRun?.data_gridded as Record<string, (number | null)[]> | undefined;
  const circumference = getWheelCircumference(calibration);

  const hasTraverse = useMemo(() => hasTraverseData(gridded), [gridded]);
  const traverseSegments = useMemo(() => buildTraverseSegments(gridded), [gridded]);
  const segmentsForPlots = dataMode === 'traverse' ? traverseSegments : undefined;

  const powerSeries = useMemo(() => buildPowerSeries(gridded, dataMode), [gridded, dataMode]);
  const speedSeries = useMemo(
    () => buildSpeedSeries(gridded, speedMode, circumference, dataMode),
    [gridded, speedMode, circumference, dataMode],
  );
  const powerComparison = useMemo(() => buildPowerComparisonScatter(gridded, dataMode), [gridded, dataMode]);
  const comparison = useMemo(
    () => buildComparisonScatter(gridded, circumference, dataMode),
    [gridded, circumference, dataMode],
  );
  const maxSpeedMs = useMemo(() => maxWheelSpeedMs(gridded, circumference), [gridded, circumference]);

  const speedModeRadio = (
    <ModeRadio
      name="auxSpeedMode"
      value={speedMode}
      options={SPEED_MODE_OPTS}
      onChange={(v) => setSpeedMode(v as SpeedMode)}
    />
  );
  const dataModeRadio = (name: string) =>
    hasTraverse ? (
      <ModeRadio name={name} value={dataMode} options={DATA_MODE_OPTS} onChange={(v) => setDataMode(v as DataMode)} />
    ) : null;

  const speedSlider = (
    <div className="cr-flex cr-items-center cr-gap-3 cr-rounded-lg cr-bg-white cr-p-4 cr-text-sm cr-shadow-md">
      <span className="cr-whitespace-nowrap cr-font-medium cr-text-gray-600">
        Min speed <span className="cr-text-gray-400">(slip-ratio plots)</span>
      </span>
      <input
        type="range"
        min={0}
        max={maxSpeedMs || 15}
        step={0.5}
        value={speedThreshold}
        onChange={(e) => setSpeedThreshold(parseFloat(e.target.value))}
        style={{ accentColor: '#03365D' }}
        className="cr-flex-1 cr-cursor-pointer"
      />
      <span className="cr-w-16 cr-whitespace-nowrap cr-tabular-nums cr-text-gray-700">
        {speedThreshold.toFixed(1)} m/s
      </span>
    </div>
  );

  if (!runId) {
    return <div className="cr-p-4 cr-text-gray-400">No run selected.</div>;
  }

  return (
    <div className="cr-space-y-6">
      <AuxSensorPlot
        title={POWER_PLOT.title}
        unit={POWER_PLOT.unit}
        series={powerSeries}
        isLoading={isPending}
        headerExtra={dataModeRadio('auxDataModePower')}
        traverseSegments={segmentsForPlots}
      />

      <CollapsibleSection title="Power Comparison" isLoading={isPending} spinnerLabel="Loading comparison…">
        <AuxComparisonPlot
          title="Power Meter Comparison"
          xLabel="Power 1 (W)"
          yLabel="Power 2 (W)"
          unit="W"
          series={powerComparison.series}
          axisMax={powerComparison.axisMax}
        />
      </CollapsibleSection>

      <AuxSensorPlot
        title="Wheel Speed"
        unit={speedUnit(speedMode)}
        series={speedSeries}
        isLoading={isPending}
        headerExtra={
          <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-6">
            {speedModeRadio}
            {dataModeRadio('auxDataModeSpeed')}
          </div>
        }
        traverseSegments={segmentsForPlots}
      />

      <CollapsibleSection title="Sensor Comparison" isLoading={isPending} spinnerLabel="Loading comparison…">
        {speedSlider}
        <AuxRatioTimePlot
          gridded={gridded}
          circumference={circumference}
          threshold={speedThreshold}
          dataMode={dataMode}
        />
        <div className="cr-grid cr-grid-cols-1 cr-gap-6 xl:cr-grid-cols-2">
          <AuxComparisonPlot
            title="Wheel Speed Sensor Comparison"
            xLabel="Wheel Speed 1 (km/h)"
            yLabel="Wheel Speed 2 / GPS (km/h)"
            unit="km/h"
            series={comparison.series}
            axisMax={comparison.axisMax}
          />
          <AuxRatioForcePlot
            gridded={gridded}
            circumference={circumference}
            threshold={speedThreshold}
            dataMode={dataMode}
          />
        </div>
      </CollapsibleSection>

      <AuxSensorPlot title={WIND_PLOT.title} unit={WIND_PLOT.unit} series={[]} inactive />
    </div>
  );
}
